/**
 * Offline store cryptography (Phase 16A, design §6.2). Web Crypto only, AES-GCM / AES-KW / PBKDF2.
 *
 * - DEK: a random AES-256-GCM key that encrypts every offline record. It is generated extractable
 *   only so it can be wrapped once; in memory it is always the non-extractable unwrapped copy.
 * - KEK: AES-KW key derived from the 6-digit PIN with PBKDF2-SHA256 (per-device salt, iterations
 *   calibrated to about 1 s). Unwrapping with a wrong PIN fails AES-KW's integrity check.
 * - Envelope: { v, iv, ct }, with the record's "store:key" as additional authenticated data so a
 *   ciphertext can't be moved to another record.
 */

export interface Envelope {
  v: 1;
  iv: string;
  ct: string;
}

/** PBKDF2 floor (OWASP 2023 for SHA-256 is 600k; slower phones are calibrated down to this floor). */
export const MIN_PBKDF2_ITERATIONS = 310_000;
const MAX_PBKDF2_ITERATIONS = 2_000_000;
const CALIBRATION_ITERATIONS = 50_000;
const TARGET_UNLOCK_MS = 1000;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function subtle(): SubtleCrypto {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error('Web Crypto is not available on this device');
  return s;
}

export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

export function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  view.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Stable, non-reversible id for a tenant + user pair (database name, registry key). */
export async function identityHash(tenantId: string, userId: string): Promise<string> {
  const digest = await subtle().digest('SHA-256', encoder.encode(`${tenantId}:${userId}`));
  return Array.from(new Uint8Array(digest).slice(0, 10)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function deriveKek(pin: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const base = await subtle().importKey('raw', encoder.encode(pin), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    base,
    { name: 'AES-KW', length: 256 },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

/** A new DEK, returned as the wrapped bytes plus the non-extractable in-memory key. */
export async function createWrappedDek(kek: CryptoKey): Promise<{ wrapped: string; dek: CryptoKey }> {
  const extractable = await subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const wrapped = await subtle().wrapKey('raw', extractable, kek, 'AES-KW');
  const dek = await unwrapDek(toBase64(wrapped), kek);
  return { wrapped: toBase64(wrapped), dek };
}

/** Throws (OperationError) when the KEK is wrong, i.e. a wrong PIN. */
export async function unwrapDek(wrapped: string, kek: CryptoKey): Promise<CryptoKey> {
  return subtle().unwrapKey('raw', fromBase64(wrapped), kek, 'AES-KW', { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

export async function encryptJson(dek: CryptoKey, value: unknown, aad: string): Promise<Envelope> {
  const iv = randomBytes(12);
  const ct = await subtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: encoder.encode(aad) },
    dek,
    encoder.encode(JSON.stringify(value)),
  );
  return { v: 1, iv: toBase64(iv), ct: toBase64(ct) };
}

/** Throws when the key, IV, ciphertext or AAD don't match. */
export async function decryptJson<T>(dek: CryptoKey, envelope: Envelope, aad: string): Promise<T> {
  const plain = await subtle().decrypt(
    { name: 'AES-GCM', iv: fromBase64(envelope.iv), additionalData: encoder.encode(aad) },
    dek,
    fromBase64(envelope.ct),
  );
  return JSON.parse(decoder.decode(plain)) as T;
}

/**
 * Pick PBKDF2 iterations so one unlock takes about a second on this device (design §3.2,
 * low-end Android). Never below the floor, so a fast device can't weaken the key.
 */
export async function calibrateIterations(now: () => number = () => performance.now()): Promise<number> {
  const started = now();
  await deriveKek('000000', randomBytes(16), CALIBRATION_ITERATIONS);
  const elapsed = Math.max(1, now() - started);
  const iterations = Math.round((CALIBRATION_ITERATIONS * TARGET_UNLOCK_MS) / elapsed);
  return Math.min(MAX_PBKDF2_ITERATIONS, Math.max(MIN_PBKDF2_ITERATIONS, iterations));
}

/** PIN rule: exactly 6 digits, not all the same digit, not a straight run (123456 / 654321). */
export function pinProblem(pin: string): string | null {
  if (!/^\d{6}$/.test(pin)) return 'Use exactly 6 digits';
  if (/^(\d)\1{5}$/.test(pin)) return 'Don\'t repeat one digit';
  const digits = pin.split('').map(Number);
  const steps = digits.slice(1).map((d, i) => d - digits[i]);
  if (steps.every((s) => s === 1) || steps.every((s) => s === -1)) return 'Don\'t use a straight run of digits';
  return null;
}

// ---- 16D D2 (R-308): second wrapped DEK under a WebAuthn PRF key ----

/** The DEK as an extractable key, only to wrap it a second time (biometric set-up after a PIN check). */
export async function unwrapDekForRewrap(wrapped: string, kek: CryptoKey): Promise<CryptoKey> {
  return subtle().unwrapKey('raw', fromBase64(wrapped), kek, 'AES-KW', { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

export async function wrapDek(extractableDek: CryptoKey, kek: CryptoKey): Promise<string> {
  return toBase64(await subtle().wrapKey('raw', extractableDek, kek, 'AES-KW'));
}

/** PRF output → HKDF-SHA256 → AES-KW key-encryption key. */
export async function deriveKekFromSecret(secret: ArrayBuffer | Uint8Array, salt: Uint8Array, info: string): Promise<CryptoKey> {
  const bytes = secret instanceof Uint8Array ? secret : new Uint8Array(secret);
  const base = await subtle().importKey('raw', bytes, 'HKDF', false, ['deriveKey']);
  return subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: encoder.encode(info) },
    base,
    { name: 'AES-KW', length: 256 },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

export async function sha256(text: string): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest('SHA-256', encoder.encode(text)));
}
